/* ============================================================
   YC Bundle Builder
   Custom element powering the "Pick Any 4" bundle page:
   - category filter tabs + client-side sorting + load more
   - N-slot selection state with variant picker modal
   - add-to-cart + straight-to-checkout with optional discount code
   ============================================================ */

if (!customElements.get('yc-bundle-builder')) {
  class YCBundleBuilder extends HTMLElement {
    connectedCallback() {
      this.bundleSize = parseInt(this.dataset.bundleSize, 10) || 4;
      this.discountCode = (this.dataset.discountCode || '').trim();
      this.moneyFormat = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
      this.visibleCount = parseInt(this.dataset.initialVisible, 10) || 12;
      this.loadStep = this.visibleCount;
      this.checkoutMode = this.dataset.checkoutMode || 'checkout';
      this.bundleVariantId = this.dataset.bundleVariantId || '';
      this.bundlePrice = parseInt(this.dataset.bundlePrice, 10) || 0;
      this.pieceLabel = this.dataset.pieceLabel || 'Piece';

      // merchant-configured personalization fields (from section blocks)
      const fieldConfigEl = this.querySelector('[data-yc-field-config]');
      try {
        this.fieldConfig = (fieldConfigEl ? JSON.parse(fieldConfigEl.textContent) : []).map((f) => ({
          ...f,
          options: (f.options || []).map((s) => String(s).trim()).filter(Boolean),
        }));
      } catch (e) {
        this.fieldConfig = [];
      }

      // collection handle -> product ids, rendered server-side per tab collection
      const mapEl = this.querySelector('[data-yc-collection-map]');
      try {
        this.collectionMap = mapEl ? JSON.parse(mapEl.textContent) : {};
      } catch (e) {
        this.collectionMap = {};
      }

      this.selection = []; // { variantId, productId, title, variantTitle, price, image, properties }
      this.activeFilter = '';
      this.shownCount = this.visibleCount;

      this.grid = this.querySelector('[data-yc-grid]');
      this.cards = Array.from(this.querySelectorAll('[data-yc-card]'));
      this.slotsWrap = this.querySelector('[data-yc-slots]');
      this.progressEl = this.querySelector('[data-yc-progress]');
      this.moreBtn = this.querySelector('[data-yc-more]');
      this.emptyEl = this.querySelector('[data-yc-empty]');
      this.sortSelect = this.querySelector('[data-yc-sort]');
      this.tabs = Array.from(this.querySelectorAll('[data-yc-tab]'));
      this.modal = this.querySelector('[data-yc-modal]');
      this.stickyBar = this.querySelector('[data-yc-sticky]');
      this.checkoutBtn = this.querySelector('[data-yc-checkout]');
      this.errorEl = this.querySelector('[data-yc-error]');

      this.tabs.forEach((tab) =>
        tab.addEventListener('click', () => this.setFilter(tab))
      );
      if (this.sortSelect)
        this.sortSelect.addEventListener('change', () => this.applySort());
      if (this.moreBtn)
        this.moreBtn.addEventListener('click', () => {
          this.shownCount += this.loadStep;
          this.applyVisibility();
        });
      this.cards.forEach((card) => {
        const btn = card.querySelector('[data-yc-add]');
        if (btn) btn.addEventListener('click', () => this.onAdd(card));
      });
      if (this.slotsWrap)
        this.slotsWrap.addEventListener('click', (e) => {
          const removeBtn = e.target.closest('[data-yc-remove]');
          if (removeBtn) this.removeAt(parseInt(removeBtn.dataset.ycRemove, 10));
        });
      if (this.checkoutBtn)
        this.checkoutBtn.addEventListener('click', () => this.checkout());
      if (this.modal) {
        this.modal.addEventListener('click', (e) => {
          if (e.target.closest('[data-yc-modal-close]') || e.target.matches('.yc-modal__overlay'))
            this.closeModal();
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.closeModal();
        });
      }

      this.applyVisibility();
      this.render();
      if (this.stickyBar) {
        setTimeout(() => this.stickyBar.classList.add('is-visible'), 50);
      }
    }

    /* ---------- money ---------- */
    formatMoney(cents) {
      const value = (cents / 100).toFixed(2);
      const [int, dec] = value.split('.');
      const withThousands = (sep) => int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      const replacements = {
        amount: withThousands(',') + '.' + dec,
        amount_no_decimals: withThousands(','),
        amount_with_comma_separator: withThousands('.') + ',' + dec,
        amount_no_decimals_with_comma_separator: withThousands('.'),
        amount_with_apostrophe_separator: withThousands("'") + '.' + dec,
      };
      return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => replacements[key] || replacements.amount);
    }

    /* ---------- filtering / sorting / load more ---------- */
    setFilter(tab) {
      this.tabs.forEach((t) => t.setAttribute('aria-pressed', t === tab ? 'true' : 'false'));
      this.activeFilter = (tab.dataset.ycTab || '').toLowerCase();
      this.shownCount = this.visibleCount;
      this.applyVisibility();
    }

    matchesFilter(card) {
      if (!this.activeFilter) return true;
      // word-prefix match so "ring" matches "rings" but not "earrings"
      const words = (card.dataset.filter || '').toLowerCase().split(/[,\s]+/).filter(Boolean);
      // collection handles the product belongs to (tabs with a collection use "collection:<handle>")
      const collections = (card.dataset.collections || '')
        .toLowerCase()
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      return this.activeFilter
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .some((needle) => {
          if (needle.startsWith('collection:')) {
            const handle = needle.slice('collection:'.length);
            const ids = this.collectionMap[handle];
            if (ids && ids.indexOf(Number(card.dataset.productId)) !== -1) return true;
            return collections.includes(handle);
          }
          return words.some((word) => word.startsWith(needle));
        });
    }

    applyVisibility() {
      const matched = this.cards.filter((c) => this.matchesFilter(c));
      this.cards.forEach((c) => (c.hidden = true));
      matched.slice(0, this.shownCount).forEach((c) => (c.hidden = false));
      if (this.moreBtn) this.moreBtn.hidden = matched.length <= this.shownCount;
      if (this.emptyEl) this.emptyEl.hidden = matched.length > 0;
    }

    applySort() {
      const mode = this.sortSelect.value;
      const sorted = [...this.cards];
      const num = (c) => parseInt(c.dataset.price, 10) || 0;
      const idx = (c) => parseInt(c.dataset.index, 10) || 0;
      if (mode === 'price-asc') sorted.sort((a, b) => num(a) - num(b));
      else if (mode === 'price-desc') sorted.sort((a, b) => num(b) - num(a));
      else if (mode === 'title-asc')
        sorted.sort((a, b) => (a.dataset.title || '').localeCompare(b.dataset.title || ''));
      else sorted.sort((a, b) => idx(a) - idx(b));
      sorted.forEach((c) => this.grid.appendChild(c));
      this.cards = sorted;
      this.applyVisibility();
    }

    /* ---------- selection ---------- */
    fieldsForCard(card) {
      // same word-prefix matching as the filter tabs; empty match = applies to all
      const words = ((card.dataset.filter || '') + ',' + (card.dataset.title || ''))
        .toLowerCase()
        .split(/[,\s]+/)
        .filter(Boolean);
      return this.fieldConfig.filter((f) => {
        const match = (f.match || '').toLowerCase().trim();
        if (!match) return true;
        return match
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .some((needle) => words.some((word) => word.startsWith(needle)));
      });
    }

    onAdd(card) {
      if (this.selection.length >= this.bundleSize) {
        this.flashProgress();
        return;
      }
      if (card.dataset.multi === 'true' || this.fieldsForCard(card).length > 0) {
        this.openModal(card);
      } else {
        this.addItem({
          variantId: card.dataset.variantId,
          productId: card.dataset.productId,
          title: card.dataset.title,
          variantTitle: '',
          sku: card.dataset.sku || '',
          price: parseInt(card.dataset.price, 10) || 0,
          image: card.dataset.image || '',
          properties: {},
        });
      }
    }

    addItem(item) {
      if (this.selection.length >= this.bundleSize) return;
      this.selection.push(item);
      this.render();
      if (this.selection.length === this.bundleSize && this.stickyBar) {
        this.stickyBar.classList.add('is-complete');
      }
    }

    removeAt(index) {
      this.selection.splice(index, 1);
      if (this.stickyBar) this.stickyBar.classList.remove('is-complete');
      this.render();
    }

    flashProgress() {
      if (!this.progressEl) return;
      this.progressEl.style.transition = 'none';
      this.progressEl.style.color = '#b3261e';
      setTimeout(() => {
        this.progressEl.style.transition = 'color 0.6s ease';
        this.progressEl.style.color = '';
      }, 400);
    }

    render() {
      // slots
      if (this.slotsWrap) {
        Array.from(this.slotsWrap.querySelectorAll('[data-yc-slot]')).forEach((slot, i) => {
          const item = this.selection[i];
          const img = slot.querySelector('.yc-slot__img');
          slot.classList.toggle('yc-slot--filled', !!item);
          if (item) {
            if (img) {
              img.src = item.image || '';
              img.alt = item.title;
              img.hidden = !item.image;
            }
            slot.title = this.describeItem(item);
          } else {
            if (img) { img.hidden = true; img.removeAttribute('src'); }
            slot.title = '';
          }
        });
      }
      // per-card selected state
      const counts = {};
      this.selection.forEach((s) => (counts[s.productId] = (counts[s.productId] || 0) + 1));
      this.cards.forEach((card) => {
        const count = counts[card.dataset.productId] || 0;
        card.classList.toggle('yc-card--selected', count > 0);
        const badge = card.querySelector('.yc-card__count');
        if (badge) badge.textContent = count > 1 ? count + '×' : '✓';
        const btn = card.querySelector('[data-yc-add]');
        if (btn) btn.disabled = this.selection.length >= this.bundleSize && count === 0;
      });
      // progress text
      if (this.progressEl) {
        this.progressEl.innerHTML = this.progressEl.dataset.template
          .replace('[count]', '<strong>' + this.selection.length + '</strong>')
          .replace('[total]', '<strong>' + this.bundleSize + '</strong>');
      }
      // sticky bar
      this.renderSticky();
      if (this.errorEl) this.errorEl.hidden = true;
    }

    renderSticky() {
      if (!this.stickyBar) return;
      const statusEl = this.stickyBar.querySelector('[data-yc-sticky-status]');
      if (statusEl) {
        statusEl.innerHTML = statusEl.dataset.template
          .replace('[count]', '<strong>' + this.selection.length + '</strong>')
          .replace('[total]', this.bundleSize);
      }
      const dots = this.stickyBar.querySelectorAll('.yc-sticky-bar__dot');
      dots.forEach((dot, i) => dot.classList.toggle('is-filled', i < this.selection.length));

      // the bundle is always a fixed price: the bundle product's price when set,
      // otherwise the configured display price — the sum of picks is only the compare-at
      const total = this.selection.reduce((sum, s) => sum + s.price, 0);
      const priceEl = this.stickyBar.querySelector('[data-yc-sticky-price]');
      if (priceEl) {
        const fixedDisplay = this.bundlePrice > 0
          ? this.formatMoney(this.bundlePrice)
          : (priceEl.dataset.placeholder || '');
        // strike the sum only for a complete bundle where the sum truly exceeds the fixed price
        const showCompare =
          this.selection.length === this.bundleSize &&
          (this.bundlePrice === 0 || total > this.bundlePrice);
        priceEl.innerHTML = showCompare
          ? '<span class="yc-sticky-bar__compare">' + this.formatMoney(total) + '</span>' + fixedDisplay
          : fixedDisplay;
      }
      if (this.checkoutBtn) {
        this.checkoutBtn.disabled = this.selection.length !== this.bundleSize;
      }
    }

    /* ---------- variant modal ---------- */
    async openModal(card) {
      if (!this.modal) return;
      this.modalCard = card;
      const panel = this.modal.querySelector('.yc-modal__panel');
      panel.querySelector('[data-yc-modal-body]').innerHTML =
        '<p class="yc-modal__price">' + (this.dataset.textLoading || 'Loading…') + '</p>';
      this.modal.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      try {
        const res = await fetch(
          (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') +
            'products/' + card.dataset.handle + '.js'
        );
        if (!res.ok) throw new Error('fetch failed');
        this.modalProduct = await res.json();
        this.buildModal();
      } catch (err) {
        panel.querySelector('[data-yc-modal-body]').innerHTML =
          '<p class="yc-modal__error">' +
          (this.dataset.textError || 'Something went wrong. Please try again.') +
          '</p>';
      }
    }

    buildModal() {
      const product = this.modalProduct;
      const body = this.modal.querySelector('[data-yc-modal-body]');
      const hasRealOptions = !(
        product.options.length === 1 &&
        (typeof product.options[0] === 'string' ? product.options[0] : product.options[0].name) === 'Title'
      );

      const optionsHtml = !hasRealOptions
        ? ''
        : product.options
            .map((opt, i) => {
              // /products/<handle>.js returns options as objects ({name, values}), older data as strings
              const optName = typeof opt === 'string' ? opt : opt.name;
              const values = [...new Set(product.variants.map((v) => v.options[i]))];
              return (
                '<fieldset class="yc-modal__option" data-yc-option-index="' + i + '">' +
                '<legend>' + this.escape(optName) + '</legend>' +
                '<div class="yc-pills">' +
                values
                  .map(
                    (v, vi) =>
                      '<button type="button" class="yc-pill' + (vi === 0 ? ' is-active' : '') + '" data-yc-pill data-value="' + this.escape(v) + '">' +
                      this.escape(v) + '</button>'
                  )
                  .join('') +
                '</div></fieldset>'
              );
            })
            .join('');

      const fields = this.fieldsForCard(this.modalCard);
      const fieldsHtml = fields
        .map((f, i) => {
          const req = f.required ? ' <span aria-hidden="true">*</span>' : '';
          if (f.type === 'pills' && f.options.length) {
            return (
              '<fieldset class="yc-modal__option" data-yc-field="' + i + '">' +
              '<legend>' + this.escape(f.label) + req + '</legend>' +
              '<div class="yc-pills yc-pills--wrap">' +
              f.options
                .map((v) => '<button type="button" class="yc-pill" data-yc-pill data-value="' + this.escape(v) + '">' + this.escape(v) + '</button>')
                .join('') +
              '</div></fieldset>'
            );
          }
          return (
            '<div class="yc-modal__option" data-yc-field="' + i + '">' +
            '<label for="yc-field-' + i + '">' + this.escape(f.label) + req + '</label>' +
            '<input type="text" id="yc-field-' + i + '" data-yc-field-input' +
            (f.placeholder ? ' placeholder="' + this.escape(f.placeholder) + '"' : '') +
            (f.maxlength ? ' maxlength="' + f.maxlength + '"' : '') +
            ' autocomplete="off" autocorrect="off"></div>'
          );
        })
        .join('');

      const image = (this.modalCard.dataset.image || (product.images && product.images[0]) || '');
      body.innerHTML =
        '<div class="yc-modal__layout">' +
        '<div class="yc-modal__media"' + (image ? '' : ' hidden') + '><img data-yc-modal-img src="' + this.escape(image) + '" alt="' + this.escape(product.title) + '" width="600" height="600"></div>' +
        '<div class="yc-modal__info">' +
        '<h3 class="yc-modal__title">' + this.escape(product.title) + '</h3>' +
        '<p class="yc-modal__price" data-yc-modal-price></p>' +
        optionsHtml +
        fieldsHtml +
        '<p class="yc-modal__error" data-yc-modal-error hidden></p>' +
        '<button type="button" class="yc-btn" data-yc-modal-add>' +
        (this.dataset.textAddToBundle || 'Add to bundle') +
        '</button>' +
        '</div></div>';

      body.querySelectorAll('[data-yc-pill]').forEach((pill) =>
        pill.addEventListener('click', () => {
          pill.parentElement.querySelectorAll('[data-yc-pill]').forEach((p) => p.classList.remove('is-active'));
          pill.classList.add('is-active');
          this.updateModalVariant();
        })
      );
      body.querySelector('[data-yc-modal-add]').addEventListener('click', () => this.confirmModal());
      this.updateModalVariant();
    }

    currentModalVariant() {
      const groups = Array.from(this.modal.querySelectorAll('[data-yc-option-index]'));
      if (!groups.length) return this.modalProduct.variants[0];
      const chosen = groups.map((g) => {
        const active = g.querySelector('[data-yc-pill].is-active');
        return active ? active.dataset.value : null;
      });
      return this.modalProduct.variants.find((v) =>
        v.options.every((o, i) => o === chosen[i])
      );
    }

    collectModalFields() {
      const fields = this.fieldsForCard(this.modalCard);
      const properties = {};
      let missing = null;
      fields.forEach((f, i) => {
        const wrap = this.modal.querySelector('[data-yc-field="' + i + '"]');
        if (!wrap) return;
        let value = '';
        const input = wrap.querySelector('[data-yc-field-input]');
        if (input) {
          value = input.value.trim();
        } else {
          const active = wrap.querySelector('[data-yc-pill].is-active');
          value = active ? active.dataset.value : '';
        }
        if (f.required && !value && !missing) missing = f.label;
        if (value) properties[f.property || f.label] = value;
      });
      return { properties, missing };
    }

    updateModalVariant() {
      const variant = this.currentModalVariant();
      const priceEl = this.modal.querySelector('[data-yc-modal-price]');
      const addBtn = this.modal.querySelector('[data-yc-modal-add]');
      const errEl = this.modal.querySelector('[data-yc-modal-error]');
      const imgEl = this.modal.querySelector('[data-yc-modal-img]');
      if (variant && variant.available) {
        const compare = variant.compare_at_price > variant.price
          ? '<span class="yc-modal__compare">' + this.formatMoney(variant.compare_at_price) + '</span> '
          : '';
        priceEl.innerHTML = compare + this.formatMoney(variant.price);
        addBtn.disabled = false;
        errEl.hidden = true;
      } else {
        priceEl.textContent = '';
        addBtn.disabled = true;
        errEl.textContent = this.dataset.textUnavailable || 'This combination is unavailable.';
        errEl.hidden = false;
      }
      if (imgEl && variant && variant.featured_image && variant.featured_image.src) {
        imgEl.src = variant.featured_image.src;
      }
    }

    confirmModal() {
      const variant = this.currentModalVariant();
      if (!variant || !variant.available) return;
      const errEl = this.modal.querySelector('[data-yc-modal-error]');
      const { properties, missing } = this.collectModalFields();
      if (missing) {
        errEl.textContent = (this.dataset.textRequired || 'Please fill in:') + ' ' + missing;
        errEl.hidden = false;
        return;
      }
      errEl.hidden = true;
      const image =
        (variant.featured_image && variant.featured_image.src) ||
        this.modalCard.dataset.image || '';
      this.addItem({
        variantId: String(variant.id),
        productId: this.modalCard.dataset.productId,
        title: this.modalProduct.title,
        variantTitle: variant.title === 'Default Title' ? '' : variant.title,
        sku: variant.sku || '',
        price: variant.price,
        image: image,
        properties: properties,
      });
      this.closeModal();
    }

    closeModal() {
      if (!this.modal || !this.modal.classList.contains('is-open')) return;
      this.modal.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
    }

    escape(str) {
      return String(str).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[c]));
    }

    describeItem(item) {
      let desc = item.title;
      if (item.variantTitle) desc += ' — ' + item.variantTitle;
      if (item.sku) desc += ' — SKU: ' + item.sku;
      const props = Object.entries(item.properties || {})
        .map(([k, v]) => k + ': ' + v)
        .join(', ');
      if (props) desc += ' (' + props + ')';
      return desc;
    }

    /* ---------- checkout ---------- */
    async checkout() {
      if (this.selection.length !== this.bundleSize || this.checkoutBusy) return;
      this.checkoutBusy = true;
      const btn = this.checkoutBtn;
      const originalLabel = btn.textContent;
      btn.textContent = this.dataset.textAdding || 'Adding…';
      btn.disabled = true;

      const bundleLabel = this.dataset.bundleLabel || 'YC Bundle';
      let items;
      if (this.checkoutMode === 'bundle' && this.bundleVariantId) {
        // one bundle-product line whose properties list the chosen pieces,
        // so checkout shows "Pick Any 4 Bundle — Piece 1: …" like a real bundle
        const properties = {};
        this.selection.forEach((s, i) => {
          properties[this.pieceLabel + ' ' + (i + 1)] = this.describeItem(s);
        });
        items = [{ id: Number(this.bundleVariantId), quantity: 1, properties: properties }];
      } else {
        // shared group id lets a cart-transform function (or bundle app) merge
        // these lines into one parent bundle at checkout
        const groupId = 'yc-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
        items = this.selection.map((s, i) => ({
          id: Number(s.variantId),
          quantity: 1,
          properties: Object.assign({}, s.properties || {}, {
            _yc_bundle: bundleLabel,
            _yc_bundle_id: groupId,
            _yc_bundle_item: i + 1 + ' of ' + this.bundleSize,
          }),
        }));
      }

      try {
        // optionally empty the cart first so the bundle is the only thing being bought
        if (this.dataset.clearCart !== 'false') {
          const clearUrl = (window.routes && routes.cart_clear_url ? routes.cart_clear_url : '/cart/clear');
          const clearRes = await fetch(clearUrl.endsWith('.js') ? clearUrl : clearUrl + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          });
          if (!clearRes.ok) throw new Error('Could not empty the cart');
        }

        const addUrl = (window.routes && routes.cart_add_url ? routes.cart_add_url : '/cart/add');
        const res = await fetch(
          addUrl.endsWith('.js') ? addUrl : addUrl + '.js',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items }),
          }
        );
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || 'Add to cart failed');

        if (this.checkoutMode === 'cart') {
          window.location.href = '/cart';
          return;
        }
        if (this.checkoutMode === 'bundle' && this.bundleVariantId) {
          // bundle product already carries the fixed price — no discount code needed
          window.location.href = '/checkout';
          return;
        }
        if (this.discountCode) {
          window.location.href =
            '/discount/' + encodeURIComponent(this.discountCode) + '?redirect=%2Fcheckout';
        } else {
          window.location.href = '/checkout';
        }
      } catch (err) {
        if (this.errorEl) {
          this.errorEl.textContent =
            (this.dataset.textError || 'Something went wrong. Please try again.') +
            (err && err.message ? ' (' + err.message + ')' : '');
          this.errorEl.hidden = false;
        }
        btn.textContent = originalLabel;
        btn.disabled = false;
        this.checkoutBusy = false;
      }
    }
  }

  customElements.define('yc-bundle-builder', YCBundleBuilder);
}

/* Smooth-scroll any [data-yc-scroll-to] trigger to its target section */
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-yc-scroll-to]');
  if (!trigger) return;
  const target = document.querySelector(trigger.dataset.ycScrollTo);
  if (target) {
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
});